/* Printed Ambition — cart-drawer.js
   AJAX cart actions + slide-in "Your investment" drawer + add-to-cart "deposit" animation:
   button fills → coin arcs into the cart icon → count rolls → drawer opens → subtotal counts up. */

(function () {
  'use strict';

  const reduceMotion = () => window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  /* PREVIEW-ONLY: the store is on Shopify's paused plan, so real adds fail.
     On the local preview (127.0.0.1 / localhost) a failed add falls back to a fake
     cart built from the form so the animation and drawer can be reviewed.
     This never runs on the live domain. */
  const PREVIEW = /^(127\.0\.0\.1|localhost)$/.test(location.hostname);

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.mockCart = null;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target === this) this.close();
        const remove = e.target.closest('[data-line-remove]');
        if (remove) this.removeLine(remove);
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.dataset.open) this.close();
      });
    }
    open() { this.dataset.open = 'true'; document.body.style.overflow = 'hidden'; }
    close() { delete this.dataset.open; document.body.style.overflow = ''; }
    async getCart() {
      if (PREVIEW && this.mockCart) return this.mockCart;
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return res.json();
    }
    async render(opts = {}) {
      try {
        const cart = await this.getCart();
        this.innerHTML = this.template(cart, opts);
        return cart;
      } catch (e) { return null; }
    }
    async removeLine(button) {
      const key = button.dataset.lineRemove;
      if (PREVIEW && this.mockCart) {
        this.mockCart = null;
        await this.render();
        setCount(0);
        return;
      }
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        this.render();
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } catch (e) { /* silent */ }
    }
    /* Count the subtotal up from $0 to its real value. */
    countUp() {
      const el = this.querySelector('[data-subtotal]');
      if (!el) return;
      const target = Number(el.dataset.value) || 0;
      if (reduceMotion() || !target) return;
      const duration = 700;
      const start = performance.now();
      el.textContent = formatMoney(0);
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        el.textContent = formatMoney(Math.round(target * eased));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }
    template(cart, opts) {
      const items = cart.items.map((item, i) => {
        const photo = findPhoto(item.properties);
        return `
        <div class="cart-line${opts.animate ? ' cart-line--enter' : ''}" style="--i:${i}">
          <div class="cart-line__media">
            ${item.image ? `<img src="${escapeAttr(item.image)}" alt="${escapeAttr(item.product_title)}">` : ''}
            ${photo ? `<img class="cart-line__you" src="${escapeAttr(photo)}" alt="Your photo" onerror="this.remove()">` : ''}
          </div>
          <div>
            <div class="cart-line__title">${escapeHtml(item.product_title)}</div>
            <div class="cart-line__variant">${escapeHtml(item.variant_title || '')}</div>
            ${this.propertiesHtml(item.properties)}
            <div class="cart-line__price">${formatMoney(item.line_price)}</div>
            <div class="cart-line__actions">
              <button class="cart-line__remove" data-line-remove="${escapeAttr(item.key)}">Remove</button>
            </div>
          </div>
        </div>`;
      }).join('');
      return `
        <div class="drawer-panel">
        <header>
          <h2>Your investment</h2>
          <button class="icon-btn" data-drawer-close aria-label="Close cart">✕</button>
        </header>
        ${cart.preview ? '<div class="drawer-preview-tag">Preview test cart: not a real order</div>' : ''}
        <div class="drawer-body">
          ${cart.items.length ? items : '<p class="muted text-center">Your cart is empty.</p>'}
        </div>
        <div class="drawer-footer">
          <div class="cart-summary__row"><span>Subtotal</span><strong data-subtotal data-value="${cart.items_subtotal_price}">${formatMoney(cart.items_subtotal_price)}</strong></div>
          ${cart.items.length ? '<a class="btn btn--full btn--shine" href="/checkout">Checkout</a>' : ''}
        </div>
        </div>
      `;
    }
    propertiesHtml(props) {
      if (!props) return '';
      return Object.entries(props)
        .filter(([k, v]) => v && !k.startsWith('_') && !isPhotoUrl(v))
        .map(([k, v]) => `<div class="cart-line__property"><strong>${escapeHtml(k)}:</strong> ${escapeHtml(v)}</div>`)
        .join('');
    }
  }
  customElements.define('ui-cart-drawer', CartDrawer);

  /* ---------- helpers ---------- */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
  const escapeAttr = escapeHtml;
  function isPhotoUrl(v) {
    return typeof v === 'string' && (/^(blob:|https?:\/\/|\/\/|\/uploads\/)/.test(v)) &&
      (/\/uploads\//.test(v) || /^blob:/.test(v) || /\.(jpe?g|png|webp|heic|heif)(\?|$)/i.test(v));
  }
  function findPhoto(props) {
    if (!props) return null;
    const hit = Object.values(props).find(isPhotoUrl);
    return hit || null;
  }
  function visibleCartIcon() {
    return Array.from(document.querySelectorAll('[data-cart-link]')).find((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight;
    });
  }

  /* Header count: roll the old number up and the new one in, like an odometer. */
  function setCount(n) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      const old = el.textContent.trim();
      el.hidden = n === 0;
      if (reduceMotion() || old === String(n)) { el.textContent = n; return; }
      el.innerHTML = `<span class="cart-count__old">${escapeHtml(old)}</span><span class="cart-count__new">${n}</span>`;
      el.classList.add('is-rolling');
      setTimeout(() => { el.classList.remove('is-rolling'); el.textContent = n; }, 450);
    });
  }

  /* The coin pops out of the button, flips, and arcs into the cart icon. */
  function flyCoin(fromEl, toEl) {
    if (reduceMotion() || !fromEl || !Element.prototype.animate) return Promise.resolve();
    const a = fromEl.getBoundingClientRect();
    const start = { x: a.left + a.width / 2, y: a.top + a.height / 2 };
    // No cart icon on screen? Aim for the top-right corner, where the drawer opens.
    const b = toEl ? toEl.getBoundingClientRect() : null;
    const end = b ? { x: b.left + b.width / 2, y: b.top + b.height / 2 } : { x: window.innerWidth - 48, y: 48 };
    const ctrl = { x: (start.x + end.x) / 2, y: Math.min(start.y, end.y) - 140 };

    const coin = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    coin.setAttribute('viewBox', '0 0 260 280');
    coin.setAttribute('class', 'coin-flight');
    coin.setAttribute('aria-hidden', 'true');
    coin.innerHTML = '<use href="#cash-coin"/>';
    document.body.appendChild(coin);

    const frames = [];
    const steps = 18;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = (1 - t) * (1 - t) * start.x + 2 * (1 - t) * t * ctrl.x + t * t * end.x;
      const y = (1 - t) * (1 - t) * start.y + 2 * (1 - t) * t * ctrl.y + t * t * end.y;
      const scale = t < 0.15 ? 0.4 + (t / 0.15) * 0.9 : 1.3 - (t - 0.15) * 1.0;
      frames.push({
        transform: `translate(${x}px, ${y}px) translate(-50%, -50%) rotateY(${t * 360}deg) scale(${scale})`,
        opacity: t > 0.92 ? 0 : 1,
        offset: t
      });
    }
    const anim = coin.animate(frames, { duration: 760, easing: 'cubic-bezier(.45,0,.55,1)', fill: 'forwards' });
    return anim.finished.catch(() => {}).then(() => coin.remove());
  }

  function bump(el) {
    if (!el || reduceMotion()) return;
    el.classList.remove('is-bumped');
    void el.offsetWidth;
    el.classList.add('is-bumped');
    el.addEventListener('animationend', () => el.classList.remove('is-bumped'), { once: true });
  }

  /* PREVIEW-ONLY: build a fake cart from what's on the product page. */
  function buildMockCart(form) {
    const variantId = form.querySelector('input[name="id"]')?.value;
    const checked = Array.from(form.querySelectorAll('[data-variant-radios] input:checked')).map((i) => i.value);
    const priceText = document.querySelector('[data-product-price]')?.textContent || '$0';
    const price = Math.round(parseFloat(priceText.replace(/[^0-9.]/g, '')) * 100) || 0;
    const img = document.querySelector('.product__gallery img');
    const photo = form.querySelector('[data-photo-upload-thumb]')?.getAttribute('src');
    const dream = form.querySelector('[name="properties[Dream Lifestyle]"]')?.value;
    const properties = {};
    if (dream) properties['Dream Lifestyle'] = dream;
    if (photo) properties['Customer Photo'] = photo;
    return {
      preview: true,
      item_count: 1,
      items_subtotal_price: price,
      items: [{
        key: 'preview:' + variantId,
        product_title: document.querySelector('.product__title')?.textContent.trim() || 'Portrait',
        variant_title: checked.join(' / '),
        image: img ? (img.currentSrc || img.src) : '',
        line_price: price,
        properties
      }]
    };
  }

  /* PREVIEW-ONLY: the paused plan marks every size "Sold out". Re-enable the button locally. */
  if (PREVIEW) {
    const unlock = () => document.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
      const fix = () => {
        if (btn.disabled && !btn.classList.contains('is-depositing')) {
          btn.disabled = false;
          btn.textContent = btn.dataset.labelAvailable || 'Add to cart';
        }
      };
      fix();
      new MutationObserver(fix).observe(btn, { attributes: true, attributeFilter: ['disabled'] });
    });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', unlock);
    else unlock();
  }

  /* ---------- open drawer from cart icon ---------- */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-cart-link]');
    if (!link) return;
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return; // let the link's href take over
    e.preventDefault();
    drawer.render();
    drawer.open();
  });

  /* ---------- add to cart: the "deposit" ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    const submit = form.querySelector('[data-add-to-cart]');
    const label = submit ? submit.textContent : '';
    if (submit) {
      submit.disabled = true;
      submit.classList.add('is-depositing');
      submit.textContent = 'Securing your portrait…';
    }
    const drawer = document.getElementById('CartDrawer');
    try {
      const fill = wait(reduceMotion() ? 0 : 480);
      const res = await fetch('/cart/add.js', { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
      if (!res.ok) {
        if (!PREVIEW || !drawer) throw new Error('Could not add to cart');
        drawer.mockCart = buildMockCart(form);
      }
      await fill;
      if (submit) submit.textContent = 'Added ✓';

      const cart = drawer ? await drawer.render({ animate: true }) : null;
      const icon = visibleCartIcon();
      await flyCoin(submit, icon);
      bump(icon);
      if (cart) {
        setCount(cart.item_count);
        if (!cart.preview) {
          setTimeout(() => document.dispatchEvent(new CustomEvent('cart:updated', { detail: { itemCount: cart.item_count } })), 500);
        }
      }
      if (drawer) {
        await wait(reduceMotion() ? 0 : 180);
        drawer.open();
        drawer.countUp();
      }
    } catch (err) {
      window.location.href = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart' : '/cart';
    } finally {
      if (submit) {
        setTimeout(() => {
          submit.classList.remove('is-depositing');
          submit.textContent = label;
          submit.disabled = false;
        }, 900);
      }
    }
  });
})();
